import { Anchor, Badge, Card, Group, Skeleton, Stack, Table, Text } from "@mantine/core";
import { IconFileInvoice } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { DashboardData } from "../../../apis/myDashboard";
import type { PlatformInvoiceStatus } from "../../../apis/platformInvoice";
import { EmptyState } from "../EmptyState";
import { InvoiceStatusBadge } from "../InvoiceStatusBadge";
import { MoneyCell } from "../MoneyCell";
import { SectionHeader } from "../SectionHeader";

interface Props {
  data?: DashboardData;
  loading: boolean;
  currency: string;
}

// ROW 3 — invoice status summary + the last five invoices (UI-SPEC §5.1).
export function InvoiceStatusCard({ data, loading, currency }: Props) {
  const navigate = useNavigate();
  const invoices = data?.invoices ?? [];
  const counts = {
    total  : invoices.length,
    sent   : invoices.filter((i) => i.status === "SENT" || i.status === "ISSUED").length,
    paid   : invoices.filter((i) => i.status === "PAID").length,
    overdue: invoices.filter((i) => i.status === "OVERDUE").length
  };

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <SectionHeader
          title="Нэхэмжлэхийн төлөв"
          action={(
            <Anchor component="button" type="button" size="xs" onClick={() => navigate("/my-billing/platform?tab=invoices")}>
              Бүгдийг харах
            </Anchor>
          )}
        />
        {loading ? (
          <Skeleton height={200} radius="sm" />
        ) : invoices.length === 0 ? (
          <EmptyState icon={<IconFileInvoice size={26} />} message="Нэхэмжлэх алга байна." />
        ) : (
          <>
            <Group gap="xs" wrap="wrap">
              <Badge variant="light" color="gray">Сүүлийн {counts.total}</Badge>
              <Badge variant="light" color="cyan">Илгээсэн {counts.sent}</Badge>
              <Badge variant="light" color="green">Төлсөн {counts.paid}</Badge>
              <Badge variant="light" color="red">Хугацаа хэтэрсэн {counts.overdue}</Badge>
            </Group>
            <Table verticalSpacing="xs" horizontalSpacing="xs" layout="fixed">
              <Table.Tbody>
                {invoices.map((inv) => (
                  <Table.Tr key={inv.no || inv.period}>
                    <Table.Td><Text size="sm" fw={500} truncate>{inv.no || "—"}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{inv.period}</Text></Table.Td>
                    <Table.Td ta="right"><MoneyCell value={inv.amount} currency={currency} size="sm" /></Table.Td>
                    <Table.Td ta="right"><InvoiceStatusBadge status={inv.status as PlatformInvoiceStatus} /></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </Card>
  );
}
